import { RichTextLine, RichTextSegment } from '../models/ns-tray-rich-text.models';

/**
 * Bag and co-pay labels for MPS batches (batches sent with bags). Pure functions shared by the
 * standard scan screen and Batch Pick. Barcodes are MPS's own and are printed exactly as sent.
 * The same layout is in the NS Tray test app (apps/prototypes/ns-tray-test-app) for printer tests.
 */

export interface MpsMoney {
  amount: number;
  currency?: string | null;
}

export interface MpsCopay extends MpsMoney {
  barcode?: string | null;
  barcodeFormat?: string | null;
}

export interface MpsBagForLabel {
  bagId: string;
  barcode: string;
  barcodeFormat?: string | null;
  price?: MpsMoney | null;
  copay?: MpsCopay | null;
}

export interface MpsMedicationForLabel {
  medicationId: string;
  medicationName?: string | null;
  bagId?: string | null;
  /** Accepted by the API but not printed: co-pay is per bag. */
  copay?: MpsCopay | null;
}

export interface MpsPrescriptionForLabel {
  rxId: string;
  patientName?: string | null;
  patientLocation?: string | null;
  issuedDate?: string | Date | null;
  medications?: MpsMedicationForLabel[] | null;
}

/** The parts of GET /v1/Batch/{id} the labels use. */
export interface MpsBatchForLabels {
  prescriptions?: MpsPrescriptionForLabel[] | null;
  bags?: MpsBagForLabel[] | null;
}

export interface BagLabelContext {
  bag: MpsBagForLabel;
  patientName: string;
  patientLocation?: string | null;
  date: Date;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const SYMBOLS: Record<string, string> = { GBP: '£', EUR: '€' };

/** Bar height on the 69×36 mm label. */
const BARCODE_HEIGHT_MM = 9;
/** 3 dots = 0.375 mm modules on the 203 DPI Zebra; NS Tray narrows long barcodes to fit. */
const BARCODE_MODULE_DOTS = 3;
/**
 * The bag label's name size, so the name stays on two lines of its 44 mm column: they hold about
 * 40 characters at 9 pt and 50 at 7.6 pt. Capitals are wider, so they count 1.25.
 */
function nameSize(name: string): number {
  const room = [...name].reduce((sum, c) => sum + (c !== c.toLowerCase() ? 1.25 : 1), 0);
  return room <= 40 ? 9 : room <= 50 ? 7.6 : 6.5;
}

export function formatMoney(money: MpsMoney): string {
  const code = (money.currency ?? '').toUpperCase();
  const amount = Number(money.amount).toFixed(2);
  const symbol = SYMBOLS[code];
  if (symbol) return `${symbol}${amount}`;
  return code ? `${code} ${amount}` : amount;
}

/** "6 Sep 19": used on the bag labels and the medicine labels alike. */
export function labelDate(date: Date): string {
  return `${date.getDate()} ${MONTHS[date.getMonth()]} ${String(date.getFullYear()).slice(-2)}`;
}

/** The barcode in the format the batch was sent with (only Code128 is accepted since 2026-10). */
function barcodeLine(value: string, format?: string | null): RichTextLine {
  return {
    barcode: { value, format: (format || 'code128').toLowerCase(), height: BARCODE_HEIGHT_MM, moduleDots: BARCODE_MODULE_DOTS },
    align: 'center',
    marginTop: 1,
  };
}

/** One bag label: copy `n` of `total` physical bags. Every copy carries the same barcode. */
export function bagLabelLines(context: BagLabelContext, n: number, total: number): RichTextLine[] {
  const lines: RichTextLine[] = [
    {
      segments: [
        { text: `${n} of ${total}`, size: 9, bold: true, width: 14, align: 'left', wrap: false, marginLeft: 2 },
        // Rows end by 60 mm: the FIT FP-32L prints only 63.1 mm across (label print test, 30 Oct).
        // A long name prints smaller, so it stays on two lines clear of the footer.
        { text: context.patientName, size: nameSize(context.patientName), bold: true, width: 44, align: 'center', wrap: true },
      ],
      offsetTop: 2,
    },
  ];

  const location = context.patientLocation?.trim();
  if (location) lines.push({ text: location, size: 7.6, align: 'center' });

  lines.push(barcodeLine(context.bag.barcode, context.bag.barcodeFormat));

  const footer: RichTextSegment[] = [
    { text: labelDate(context.date), size: 7.6, width: 20, align: 'left', wrap: false, marginLeft: 2 },
  ];
  if (context.bag.price) {
    footer.push({ text: `Price ${formatMoney(context.bag.price)}`, size: 7.6, width: 38, align: 'right', wrap: false });
  }
  lines.push({ segments: footer, valign: 'bottom', offsetBottom: 2, segmentValign: 'bottom' });

  return lines;
}

/** The co-pay label for a bag. */
export function copayLabelLines(patientName: string, copay: MpsCopay): RichTextLine[] {
  const lines: RichTextLine[] = [
    { text: patientName, size: 9, bold: true, align: 'center', offsetTop: 2 },
  ];
  lines.push({ text: `Co-payment ${formatMoney(copay)}`, size: 9, bold: true, align: 'center' });
  if (copay.barcode) lines.push(barcodeLine(copay.barcode, copay.barcodeFormat));
  return lines;
}

function parseDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Every label for one bag: `physicalBags` bag labels ("1 of N" … "N of N"), then one co-pay
 * label if the bag has a co-pay. Co-pay is per bag (McLernons); co-pays sent on items aren't
 * printed. The date is the latest issue date of the bag's prescriptions (a bag can span
 * prescriptions), or today if none has one.
 */
export function bagLabelJobs(batch: MpsBatchForLabels, bagId: string, physicalBags: number): RichTextLine[][] {
  const bag = (batch.bags ?? []).find(b => b.bagId === bagId);
  if (!bag) return [];

  const prescriptions = [...new Set(bagLines(batch, bagId).map(l => l.rx))];

  const patientName = bagPatientName(batch, bagId);
  const patientLocation = prescriptions.map(rx => rx.patientLocation).find(l => !!l?.trim()) ?? null;
  const dates = prescriptions.map(rx => parseDate(rx.issuedDate)).filter((d): d is Date => d !== null);
  const date = dates.length ? new Date(Math.max(...dates.map(d => d.getTime()))) : new Date();

  const jobs: RichTextLine[][] = [];
  for (let n = 1; n <= physicalBags; n++) {
    jobs.push(bagLabelLines({ bag, patientName, patientLocation, date }, n, physicalBags));
  }
  if (bag.copay) jobs.push(copayLabelLines(patientName, bag.copay));
  return jobs;
}

interface BagLine {
  rx: MpsPrescriptionForLabel;
  med: MpsMedicationForLabel;
}

/** The lines (with their prescription) packed into one bag, in batch order. */
export function bagLines(batch: MpsBatchForLabels, bagId: string): BagLine[] {
  return (batch.prescriptions ?? []).flatMap(rx =>
    (rx.medications ?? []).filter(m => m.bagId === bagId).map(med => ({ rx, med })));
}

/** Whose bag it is: the patient of its first line. */
export function bagPatientName(batch: MpsBatchForLabels, bagId: string): string {
  return (bagLines(batch, bagId)[0]?.rx.patientName ?? '').trim();
}

export interface BagProgress {
  bagId: string;
  patientName: string;
  done: number;
  total: number;
}

/** Per bag (in batch order): how many of its lines pass `isDone`, and whose bag it is. */
export function bagProgress(
  batch: MpsBatchForLabels,
  isDone: (rxId: string, medicationId: string) => boolean,
): BagProgress[] {
  return (batch.bags ?? []).map(bag => {
    const lines = bagLines(batch, bag.bagId);
    return {
      bagId: bag.bagId,
      patientName: bagPatientName(batch, bag.bagId),
      done: lines.filter(l => isDone(l.rx.rxId, l.med.medicationId)).length,
      total: lines.length,
    };
  });
}

/** Bags (in batch order) whose every line passes `isDone`. Lines without a bag are ignored. */
export function completeBagIds(
  batch: MpsBatchForLabels,
  isDone: (rxId: string, medicationId: string) => boolean,
): string[] {
  return bagProgress(batch, isDone).filter(b => b.total > 0 && b.done === b.total).map(b => b.bagId);
}
